import * as React from 'react';
import { cn } from '../../lib/utils';
import { Icon } from '../icon';
import type { IconName } from '../icon';
import { BodyS } from '../typography';

export interface IDropzoneProps {
	'browseInputId': string;
	'acceptedFileTypes': string;
	'maxFileSize': string;
	'downloadHref': string;
	'handleOnDrop': (event: React.DragEvent<HTMLDivElement>) => void;
	'handleDragOver': (event: React.DragEvent<HTMLDivElement>) => void;
	'handleDragLeave': (event: React.DragEvent<HTMLDivElement>) => void;
	'onDownloadClick'?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
	'downloadFileName'?: string;
	'icon'?: IconName;
	'isDisabled'?: boolean;
	'isDragOver'?: boolean;
	'error'?: string;
	'data-testid'?: string;
	'download-data-testid'?: string;
}

function getDropzoneStateClassName(
	isDisabled: boolean,
	isDragOver: boolean
): string {
	if (isDisabled) {
		return 'opacity-50 cursor-not-allowed';
	}
	if (isDragOver) {
		return 'border-interactive bg-interactive-light';
	}
	return 'border-zinc-300 hover:border-interactive hover:bg-interactive-light/50';
}

export function Dropzone({
	browseInputId,
	acceptedFileTypes,
	maxFileSize,
	downloadHref,
	handleOnDrop,
	handleDragOver,
	handleDragLeave,
	onDownloadClick,
	downloadFileName,
	icon,
	isDisabled = false,
	isDragOver = false,
	error,
	'data-testid': testId,
	'download-data-testid': downloadTestId,
}: Readonly<IDropzoneProps>) {
	const stateClassName = getDropzoneStateClassName(isDisabled, isDragOver);

	return (
		<>
			<div
				data-testid={testId}
				aria-disabled={isDisabled || undefined}
				onDrop={isDisabled ? undefined : handleOnDrop}
				onDragOver={isDisabled ? undefined : handleDragOver}
				onDragLeave={isDisabled ? undefined : handleDragLeave}
				className={cn(
					'flex flex-col items-center justify-center gap-8 rounded-m border border-dashed px-16 py-24 transition-colors',
					'text-text-secondary',
					stateClassName,
					error && 'border-danger'
				)}
			>
				<label
					htmlFor={isDisabled ? undefined : browseInputId}
					className={cn(
						'flex w-full flex-col items-center justify-center gap-8',
						!isDisabled &&
							'cursor-pointer focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-outline-interactive'
					)}
				>
					<Icon
						name={icon ?? 'cloudUpload'}
						size="l"
						className="text-text-link"
					/>
					<span className="text-14">
						Drag your file(s) here or{' '}
						<span className="text-text-link font-medium underline">browse</span>
					</span>
				</label>

				<BodyS color="secondary">
					Only supports {acceptedFileTypes} files matching our template, up to{' '}
					{maxFileSize}.{' '}
					<a
						href={isDisabled ? undefined : downloadHref}
						download={downloadFileName}
						aria-disabled={isDisabled || undefined}
						tabIndex={isDisabled ? -1 : undefined}
						data-testid={downloadTestId}
						className={cn(
							'text-text-link underline',
							isDisabled ? 'pointer-events-none' : 'cursor-pointer'
						)}
						onClick={(event) => {
							event.stopPropagation();
							if (isDisabled) {
								event.preventDefault();
								return;
							}
							onDownloadClick?.(event);
						}}
					>
						Download template
					</a>
				</BodyS>
			</div>
			{error && <span className="text-12 text-text-danger">{error}</span>}
		</>
	);
}
